/**
 * One line to the technician, one line back.
 *
 * The hosted path posts the brief, the recent turns and the question to the
 * proxy (`/api/talk`, functions/index.js), which holds the system prompt and
 * the provider key and returns the technician's reply. The proxy never sees
 * anything the brief does not carry, and the client never composes the
 * prompt, so a student cannot loosen the rules from devtools.
 *
 * Every failure - offline, timeout, no key, a 5xx - lands on `localReply`,
 * which answers from the same brief with template sentences. Same shape as
 * the assistant's routing fallback: a classroom with no network still has
 * a technician who turns, talks, and reads the screen out.
 */

import { t } from '../i18n/i18n'
import { guessRegion, storedRegion } from '../assistant/llm/region'
import { storedProvider } from '../assistant/llm/provider'
import type { ExperimentReport } from '../assistant/types'
import type { TalkBrief } from './brief'
import { matchDemo, takeDemo } from './demos'
import { takeRun } from './experiments'
import { fitBrief, LEGACY_MAX_BRIEF } from './fitBrief'
import { takeAsk } from './predict'

export interface TalkTurn {
  role: 'student' | 'technician'
  text: string
  /** True when the template fallback answered rather than the model. */
  local?: boolean
  /** A question the student called off before it was answered. */
  stopped?: boolean
  /**
   * A scene this line put on the screen, by id (demos.ts). Set only from
   * the whitelist the brief carried, never from the reply's own say-so.
   */
  demo?: string
  /**
   * The measured result of an experiment this technician ran on their tool
   * (experiments.ts). Rendered by the same ReportView the tool's own panel
   * uses, because it is the same report.
   */
  report?: ExperimentReport
  /** True while that experiment is still running. */
  running?: boolean
}

export interface TalkReply {
  text: string
  local: boolean
  demo?: string
  /**
   * A question this technician is about to run on their tool, in their own
   * words. Set only where the brief said running was possible; the tool's
   * assistant still has to route it to a registered intent.
   */
  run?: string
  /**
   * What she wants them to PREDICT before that run reports back. Set only
   * alongside `run`, and it holds the report until they answer or ask to
   * skip it. See predict.ts.
   */
  ask?: string
}

const ENDPOINT = '/api/talk'
const TIMEOUT_MS = 15_000
/** How much of the conversation goes over the wire. */
const HISTORY_TURNS = 12

/**
 * `stop` is the student pressing Escape while the reply is in flight.
 *
 * A cancelled question returns NULL rather than the template answer: every
 * other failure here is the network letting the student down, and falling
 * back to a canned sentence is the right kindness, but a student who
 * stopped the question does not want an answer to it at all.
 */
export async function askTechnician(
  brief: TalkBrief,
  history: TalkTurn[],
  question: string,
  stop?: AbortSignal,
): Promise<TalkReply | null> {
  const abort = new AbortController()
  const relay = (): void => abort.abort()
  stop?.addEventListener('abort', relay)
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS)
  try {
    const post = (cap?: number): Promise<Response> =>
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        signal: abort.signal,
        body: JSON.stringify({
          brief: fitBrief(brief, cap),
          history: history.slice(-HISTORY_TURNS).map((h) => ({ role: h.role, text: h.text })),
          question,
          provider: storedProvider(),
          override: storedRegion(),
          guess: guessRegion(),
        }),
      })
    let res = await post()
    // A proxy older than this client still holds the smaller cap, and says
    // 413 to a full brief. Ask again, fitted to the cap it had: the
    // technician forgets a little rather than going offline entirely, and
    // the order in which hosting and functions are deployed stops mattering.
    if (res.status === 413) res = await post(LEGACY_MAX_BRIEF)
    if (!res.ok) throw new Error(`talk ${res.status}`)
    const data = (await res.json()) as { reply?: unknown }
    if (typeof data.reply !== 'string' || !data.reply.trim()) throw new Error('empty reply')
    // The reply may end by NAMING a scene. It is resolved here, against the
    // very list the brief sent out, and every marker leaves the sentence
    // either way - the line is about to be spoken aloud. See demos.ts.
    const taken = takeDemo(data.reply.trim(), brief.demos.map((d) => d.id))
    // And it may end by saying it is going to RUN something. Both markers
    // come out of the sentence whether they resolved or not.
    const ran = takeRun(taken.text, brief.experiments.canRun)
    // And it may end by asking the student WHICH WAY IT GOES before the tool
    // reports back. Only where a run was actually taken: without one there is
    // no observation coming to hold it against. See predict.ts.
    const pre = takeAsk(ran.text, ran.run !== undefined)
    if (!pre.text) throw new Error('empty reply')
    return { text: pre.text, local: false, demo: taken.demo, run: ran.run, ask: pre.ask }
  } catch {
    // Told to stop, rather than failed: no answer, canned or otherwise.
    if (stop?.aborted) return null
    return localAnswer(brief, question)
  } finally {
    clearTimeout(timer)
    stop?.removeEventListener('abort', relay)
  }
}

/**
 * The offline technician's whole answer: a sentence, and possibly a scene.
 *
 * The scene is the one thing the template path can offer that is not a
 * template. Every solver in demos.ts is local, so a classroom with no
 * network still gets the simulation running - only the sentence introducing
 * it comes out of the catalogue rather than out of a model.
 *
 * ORDER MATTERS HERE. A scene is offered only where the templates were
 * STUMPED - it replaces "I can't tell you that from here", never a real
 * answer. Matched first, "where is the deposition tool?" would have put up a
 * film-growth simulation instead of pointing at bay 06, because the word the
 * student used to name a tool is also the word that names the science.
 */
function localAnswer(brief: TalkBrief, question: string): TalkReply {
  const answer = localReply(brief, question)
  // The refusal is the ONE reply a scene may displace, and it is recognised
  // by being the very sentence `cantSay` builds - one helper, used in both
  // places, so the test cannot drift away from the text it tests for.
  if (answer !== cantSay(brief)) return { text: answer, local: true }
  const demo = matchDemo(
    brief.demos.map((d) => d.id),
    question,
  )
  if (!demo) return { text: answer, local: true }
  const title = brief.demos.find((d) => d.id === demo)?.title ?? ''
  return { text: t('talk.local.Showing', { title }), local: true, demo }
}

/**
 * "I can't tell you that from here." The template technician's refusal, and
 * the marker `localAnswer` looks for to know it was stumped.
 */
function cantSay(brief: TalkBrief): string {
  return brief.tool
    ? t('talk.local.CantSay', { tag: brief.tool.tag })
    : t('talk.local.CantSayNoTool')
}

/**
 * "Are you a person?", in the four languages the app speaks - and ONLY that
 * question.
 *
 * Word-anchored on purpose. `localReply` below matches whole words too now,
 * but with one generic helper; these need more than that: "ai" is
 * inside "said", "again" and "detail", and "model" is what a tool's
 * nameplate carries, so a substring test here would answer this question in
 * place of the one the student actually asked.
 */
const ASKS_WHO_IS_ANSWERING = new RegExp(
  [
    // What is answering, named.
    String.raw`\b(a\.?i\.?|llm|chat ?bot|chat ?gpt|gpt|deepseek|language model)\b`,
    // "are you a person", "r u human", "are you real".
    String.raw`\b(are|r) (you|u) (an? )?(real )?(person|human|bot|robot|machine|program|alive)\b`,
    // "what AI is this" - but NOT "what model is this pump", which is a
    // question about a nameplate. "model" only counts when it is asked
    // about the listener.
    String.raw`\b(which|what) (ai|llm)\b`,
    String.raw`\b(model|llm|ai) (are|r) (you|u)\b`,
    String.raw`inteligencia artificial|eres (un[ao]? )?(ia|robot|persona|humano|m[áa]quina)|qu[ée] modelo (eres|usas|est[áa]s usando)|modelo de lenguaje`,
    String.raw`人工智[能慧]|机器人|機器人|真人|语言模型|語言模型|什[么麼]模型|你是人`,
  ].join('|'),
  'i',
)

/**
 * The template technician. Keyword-matched, from the brief and nothing else,
 * in the student's language. Deliberately dull: it exists so the person is
 * never mute, not to pass for the model.
 */
export function localReply(brief: TalkBrief, question: string): string {
  const q = question.toLowerCase()
  // WHOLE WORDS for anything written in letters, substrings only for CJK,
  // which has no word boundaries to find. It was `includes` for everything,
  // and "rate" is inside "vibrate", "number" inside "wavenumber", "pm"
  // inside "development" and "fix" inside "prefix" - so a student asking how
  // a Raman wavenumber shifts was read the screen (2026-09-18).
  const has = (...words: string[]) =>
    words.some((w) =>
      /^[\p{Script=Latin}\s'-]+$/u.test(w)
        ? new RegExp(`(?<![\\p{L}\\p{N}])${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\p{L}\\p{N}])`, 'u').test(q)
        : q.includes(w),
    )
  const tag = brief.tool?.tag ?? ''
  const cant = cantSay(brief)
  // Whether one of this person's scenes answers the question. Two templates
  // below are greedy - "what is ..." recites the tool's job description and
  // "where ..." recites the hall - and both were swallowing questions about
  // physics: "what is a quantum well?" and "where does the boron sit in the
  // lattice?". A template that knows nothing about the question steps aside
  // for a scene that does, unless the question is plainly about the tool or
  // about finding something on the floor.
  const aScene = (): boolean => matchDemo((brief.demos ?? []).map((d) => d.id), question) !== undefined
  const aboutOwnTool = (): boolean =>
    !!brief.tool && [brief.tool.tag, brief.tool.name, brief.tool.process].some((s) => s && q.includes(s.toLowerCase()))
  const aboutThePlace = (): boolean =>
    /\b[a-z]{2,5}-\d{2}\b/i.test(question) ||
    has('bay', 'tool', 'tools', 'machine', 'room', 'stocker', 'stored', 'kept', 'exit', 'gowning', 'aisle', 'chase', 'subfab', 'layout', 'which bay') ||
    has('bahía', 'bahia', 'herramienta', 'máquina', 'maquina', 'sala', 'pasillo', 'salida', 'almacén', 'almacen') ||
    has('工作区', '工作區', '机台', '機台', '设备', '設備', '房间', '房間', '出口', '走道', '存放') ||
    (brief.floor?.directory ?? []).some((d) => d.process && q.includes(d.process.toLowerCase()))

  // WHO IS ANSWERING, asked before anything about the fab. It has to come
  // first: "what are you" is a keyword of the "what are you doing" branch
  // below, which would answer a student asking whether they are talking to
  // a person by telling them what the screen says.
  //
  // The hosted technician NAMES the model that wrote the line (see the rule
  // in functions/talkPrompt.js). This path has no model to name, because
  // there is no model: every sentence here is a template. Saying that is
  // the honest answer, not a lesser version of the hosted one.
  if (ASKS_WHO_IS_ANSWERING.test(question)) return t('talk.local.NotHuman')

  // A "why", a "how", a "what if" is a mechanism question, and the brief
  // holds none: checked first, before a word like "rate" in it can match
  // the screen readout below.
  if (has('why', 'how come', 'what if', 'what would', 'cause', 'because', 'por qué', 'porqué', 'qué pasaría', '为什么', '為什麼', '怎么', '怎麼', '原因')) {
    return cant
  }
  // "What is CMP" is the job description, and the catalogue has a line
  // for it - the one thing besides the screen a technician can explain.
  if (brief.tool && has('what is', "what's", 'what does', 'explain', 'tell me about', 'qué es', 'que es', 'explica', '什么是', '是什么', '是什麼', '什麼是', '介绍', '介紹')) {
    // Unless it is a question one of their scenes answers and it is not
    // about the tool itself: then the refusal, which lets the scene up.
    if (!aboutOwnTool() && aScene()) return cant
    // The first sentence only: the catalogue line goes on to say what is
    // simulated, which is documentation, not something she would say.
    const blurb = brief.tool.blurb.split(/(?<=[.。])\s*/)[0] ?? ''
    return t('talk.local.About', { tag, name: brief.tool.name, process: brief.tool.process, blurb })
  }
  if (has('order', 'task', 'objective', 'orden', 'tarea', '工单', '任务')) {
    if (!brief.workOrder) return t('talk.local.NoOrder')
    const done = brief.workOrder.objectives.filter((o) => o.met).length
    return t('talk.local.Order', { title: brief.workOrder.title, done, total: brief.workOrder.objectives.length })
  }
  if (has('where', 'find', 'which bay', 'layout', 'dónde', 'donde', '哪里', '哪裡', '在哪')) {
    // "Where does the boron sit in the lattice?" is not a request for
    // directions. "Where is the deposition tool?" is, even though it names a
    // science a scene could show - so the place wins whenever it is named.
    if (!aboutThePlace() && aScene()) return cant
    return brief.floor.layout
  }
  if (brief.tool && brief.screen) {
    if (has('down', 'pm', 'maintenance', 'broken', 'fix', 'fixed', 'repair', 'mantenimiento', '维修', '維修', '保养', '保養')) {
      if (brief.floor.down.includes(tag)) return t('talk.local.Down', { tag })
    }
    if (has('screen', 'reading', 'number', 'recipe', 'running', 'rate', 'pantalla', 'lectura', '屏幕', '螢幕', '读数', '讀數')) {
      // "How does the activation energy set the diffusion rate?" is not a
      // request to read the screen out.
      if (!aboutOwnTool() && aScene()) return cant
      const rows = brief.screen.rows.map(([k, v]) => `${k}: ${v}`).join(', ')
      return t('talk.local.Screen', { headline: brief.screen.headline, caption: brief.screen.caption, rows })
    }
    if (has('doing', 'working', 'up to', 'what are you', 'haciendo', '在做', '做什么', '做什麼')) {
      return t('talk.local.Doing', {
        tag,
        state: t(`talk.local.States.${brief.screen.state}`),
        headline: brief.screen.headline,
        caption: brief.screen.caption,
      })
    }
  } else if (has('doing', 'working', 'going', 'haciendo', '在做', '去哪')) {
    return t('talk.local.DoingNoTool')
  }
  return cant
}
